import { useState } from "react";
import { FileText } from "lucide-react";

import { useGetRecordMediaQuery } from "@/api/api";
import { PhotoAlbum } from "./PhotoAlbum";
import { MediaLightbox } from "./MediaLightbox";
import { formatBytes } from "./MediaInput";

// Presigned URLs expire (default TTL 1h) — refetch when a mount is older
// than 50 minutes so long-lived views keep working
const REFETCH_AFTER_SECONDS = 3000;

export function RecordMediaSection({ recordId }: { recordId: string }) {
  const { data: media } = useGetRecordMediaQuery(recordId, {
    refetchOnMountOrArgChange: REFETCH_AFTER_SECONDS,
  });
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  if (!media || media.length === 0) return null;

  const images = media.filter((m) => m.media_type === "image");
  const audio = media.filter((m) => m.media_type === "audio");
  const documents = media.filter((m) => m.media_type === "document");

  return (
    <div className="flex flex-col gap-3">
      {images.length > 0 && (
        <PhotoAlbum images={images} onOpen={setLightboxIndex} />
      )}

      {audio.length > 0 && (
        <div className="flex flex-col gap-2">
          {audio.map((m) => (
            <div key={m.id} className="flex flex-col gap-1">
              <div className="truncate font-mono text-[11px] text-muted-foreground">
                {m.original_filename}
              </div>
              <audio
                controls
                preload="none"
                src={m.url}
                className="h-9 w-full"
              />
            </div>
          ))}
        </div>
      )}

      {documents.length > 0 && (
        <div className="flex flex-col gap-1">
          {documents.map((m) => (
            <a
              key={m.id}
              href={m.download_url}
              className="flex items-center gap-2 rounded-md border border-border bg-card px-2 py-1.5 hover:bg-accent"
            >
              <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1 truncate text-[13px] text-foreground">
                {m.original_filename}
              </span>
              <span className="shrink-0 font-mono text-[10.5px] text-muted-foreground">
                {formatBytes(m.size_bytes)}
              </span>
            </a>
          ))}
        </div>
      )}

      <MediaLightbox
        images={images}
        index={lightboxIndex}
        onIndexChange={setLightboxIndex}
      />
    </div>
  );
}
